import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

const readJson = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (error) {
    console.error(`Error parsing ${key} from localStorage:`, error);
    return fallback;
  }
};

export const UserProvider = ({ children }) => {
  const [globalFunctionalities, setGlobalFunctionalities] = useState(() => readJson('globalFunctionalities', []));
  const [incubationAccess,      setIncubationAccess]      = useState(() => readJson('incubationAccess', {}));
  const [userAccess,            setUserAccess]            = useState(() => readJson('userAccess', null));
  const [currentUser,           setCurrentUser]           = useState(() => readJson('currentUser', null));

  useEffect(() => {
    try { localStorage.setItem('globalFunctionalities', JSON.stringify(globalFunctionalities)); }
    catch (e) { console.error('Error saving globalFunctionalities:', e); }
  }, [globalFunctionalities]);

  useEffect(() => {
    try { localStorage.setItem('incubationAccess', JSON.stringify(incubationAccess)); }
    catch (e) { console.error('Error saving incubationAccess:', e); }
  }, [incubationAccess]);

  useEffect(() => {
    try {
      if (userAccess) localStorage.setItem('userAccess', JSON.stringify(userAccess));
      else localStorage.removeItem('userAccess');
    } catch (e) { console.error('Error saving userAccess:', e); }
  }, [userAccess]);

  useEffect(() => {
    try {
      if (currentUser) localStorage.setItem('currentUser', JSON.stringify(currentUser));
      else localStorage.removeItem('currentUser');
    } catch (e) { console.error('Error saving currentUser:', e); }
  }, [currentUser]);

  const setGlobalsFromApi = (accessData) => {
    setUserAccess(accessData);
    if (accessData && Array.isArray(accessData.functionality_names)) {
      setGlobalFunctionalities(accessData.functionality_names);
    } else {
      setGlobalFunctionalities([]);
    }
  };

  const setIncubationAccessFromApi = (apiPayload) => {
    const next = {};
    const incMap = apiPayload?.incubation_access || {};
    for (const [incubationId, entry] of Object.entries(incMap)) {
      next[incubationId] = Array.isArray(entry?.functionality_names) ? entry.functionality_names : [];
    }
    setIncubationAccess(next);
  };

  const clearUserData = () => {
    setGlobalFunctionalities([]);
    setIncubationAccess({});
    setUserAccess(null);
    setCurrentUser(null);
    try {
      localStorage.removeItem('globalFunctionalities');
      localStorage.removeItem('incubationAccess');
      localStorage.removeItem('userAccess');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('functionalityNames');
      localStorage.removeItem('incubationIds');
    } catch (e) { console.error('Error clearing localStorage:', e); }
  };

  const hasGlobalFunctionality = (name) => globalFunctionalities.includes(name);
  const isDashboardAdmin       = () => globalFunctionalities.includes('admin_dashboard');
  const hasIncubationAccess    = (incubationId) => incubationId != null && Object.prototype.hasOwnProperty.call(incubationAccess, String(incubationId));
  const hasIncubationPermission = (incubationId, name) => {
    if (isDashboardAdmin()) return true;
    if (incubationId == null) return false;
    const perms = incubationAccess[String(incubationId)];
    return Array.isArray(perms) && perms.includes(name);
  };
  const getAccessibleIncubationIds = () => Object.keys(incubationAccess);

  const value = {
    globalFunctionalities,
    incubationAccess,
    userAccess,
    currentUser,

    setGlobalFunctionalities,
    setIncubationAccess,
    setUserAccess,
    setCurrentUser,

    setGlobalsFromApi,
    setIncubationAccessFromApi,
    clearUserData,

    hasGlobalFunctionality,
    isDashboardAdmin,
    hasIncubationAccess,
    hasIncubationPermission,
    getAccessibleIncubationIds
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
